import { ApiError, storedSite, sessionHeaders } from '../../api';

export interface Transaction {
  readonly id: string;
  readonly parentId: string | null;
  readonly provider: string;
  readonly kind: 'authorize' | 'capture' | 'charge' | 'refund';
  readonly method: string | null;
  readonly orderId: string | null;
  readonly customerId: string | null;
  readonly amountMinor: number;
  readonly currency: string;
  readonly status: 'pending' | 'authorized' | 'captured' | 'failed' | 'refunded';
  readonly externalId: string | null;
  readonly failureReason: string | null;
  readonly failureCode: string | null;
  readonly createdAt: string;
}

export interface TransactionPage {
  readonly items: readonly Transaction[];
  readonly nextCursor: string | undefined;
}

export interface Invoice {
  readonly id: string;
  readonly number: string;
  readonly customerId: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly status: 'issued' | 'paid' | 'cancelled';
  readonly dueAt: string | null;
  readonly paidAt: string | null;
  readonly createdAt: string;
}

export type StatementOutcome =
  | 'matched'
  | 'already_paid'
  | 'amount_mismatch'
  | 'payer_mismatch'
  | 'not_found';

export interface StatementLine {
  readonly docNumber: string;
  readonly date: string;
  readonly amountMinor: number;
  readonly payerBin: string | null;
  readonly payerName: string | null;
  readonly purpose: string;
  readonly outcome: StatementOutcome;
  readonly invoiceId: string | null;
  readonly invoiceNumber: string | null;
  readonly repeated: boolean;
}

export interface StatementImport {
  readonly id: string;
  readonly fileName: string;
  readonly sha256: string;
  readonly provider: string;
  readonly account: string | null;
  readonly periodFrom: string | null;
  readonly periodTo: string | null;
  readonly counts: {
    readonly documents: number;
    readonly incoming: number;
    readonly matched: number;
    readonly alreadyPaid: number;
    readonly amountMismatch: number;
    readonly payerMismatch: number;
    readonly notFound: number;
    readonly repeated: number;
  };
  readonly createdAt: string;
}

export interface StatementImportResult {
  readonly import: StatementImport;
  readonly lines: readonly StatementLine[];
  readonly replayed: boolean;
}

export interface FiscalDocument {
  readonly id: string;
  readonly provider: string;
  readonly kind: 'receipt' | 'refund';
  readonly orderId: string | null;
  readonly amountMinor: number;
  readonly status: 'queued' | 'issued' | 'failed' | 'skipped';
  readonly offline: boolean;
  readonly fiscalNumber: string | null;
  readonly ofdReceiptId: string | null;
  readonly attempts: number;
  readonly lastError: string | null;
  readonly transmittedAt: string | null;
  readonly createdAt: string;
}

export interface TransmitResult {
  readonly checked: number;
  readonly transmitted: number;
  readonly failed: number;
}

export interface ReconciliationFinding {
  readonly id: string;
  readonly kind: string;
  readonly transactionId: string | null;
  readonly externalId: string | null;
  readonly systemMinor: number | null;
  readonly providerMinor: number | null;
  readonly fiscalMinor: number | null;
}

export interface ReconciliationReport {
  readonly run: {
    readonly id: string;
    readonly provider: string;
    readonly businessDay: string;
    readonly startedAt: string;
    readonly systemCount: number;
    readonly providerCount: number;
    readonly fiscalCount: number;
    readonly findingsCount: number;
    readonly status: string;
  };
  readonly findings: readonly ReconciliationFinding[];
}

export interface ProviderView {
  readonly code: string;
  readonly kind: 'acquiring' | 'wallet' | 'invoice' | 'fiscal';
  readonly adapter: string;
  readonly title: string;
  readonly endpoint: string | null;
  readonly credentialEnv: string | null;
  readonly siteId: string | null;
  readonly timeoutMs: number;
  readonly status: 'active' | 'paused';
  readonly settings?: Readonly<Record<string, string>>;
}

export interface ProviderHealth {
  readonly provider: string;
  readonly kind: string;
  readonly adapter: string;
  readonly available: boolean;
  readonly since: string;
  readonly lastError: string | null;
  readonly configured: boolean;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  idempotencyKey?: string,
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  Object.assign(headers, sessionHeaders());
  const site = storedSite();
  if (site) headers['x-moika-site'] = site;
  if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;

  const res = await fetch(path, {
    method,
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    throw new ApiError(
      String(payload['error'] ?? 'E00.internal'),
      String(payload['message'] ?? payload['error'] ?? 'Ошибка запроса'),
      res.status,
    );
  }
  return payload['data'] as T;
}

export function newIdempotencyKey(): string {
  return `admin-${crypto.randomUUID()}`;
}

export function transactions(query: { order?: string; customer?: string } = {}): Promise<TransactionPage> {
  const params = new URLSearchParams();
  if (query.order) params.set('order_id', query.order);
  if (query.customer) params.set('customer_id', query.customer);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return request<TransactionPage>('GET', `/api/v1/payments/transactions${suffix}`);
}

export function refundTransaction(
  transactionId: string,
  amountMinor?: number,
): Promise<unknown> {
  return request(
    'POST',
    '/api/v1/payments/refund',
    { transactionId, ...(amountMinor === undefined ? {} : { amountMinor }) },
    newIdempotencyKey(),
  );
}

export function invoices(): Promise<readonly Invoice[]> {
  return request<readonly Invoice[]>('GET', '/api/v1/payments/invoices');
}

/** Единый счёт юрлицам за месяц `YYYY-MM` (М4.8): то же, что делает задача в начале месяца. */
export function consolidateInvoices(period: string): Promise<{ readonly invoices: readonly Invoice[] }> {
  return request('POST', '/api/v1/payments/invoices/consolidate', { period });
}

export function payInvoice(id: string, provider: string): Promise<unknown> {
  return request('POST', `/api/v1/payments/invoices/${id}/pay`, { provider }, newIdempotencyKey());
}

export function importStatement(fileName: string, content: string): Promise<StatementImportResult> {
  return request<StatementImportResult>('POST', '/api/v1/payments/invoices/statements', {
    fileName,
    content,
  });
}

export function statementImports(): Promise<readonly StatementImport[]> {
  return request<readonly StatementImport[]>('GET', '/api/v1/payments/invoices/statements');
}

export function fiscalDocuments(pendingOnly: boolean): Promise<readonly FiscalDocument[]> {
  return request<readonly FiscalDocument[]>(
    'GET',
    `/api/v1/payments/fiscal${pendingOnly ? '?pending=true' : ''}`,
  );
}

export function retryFiscal(): Promise<TransmitResult> {
  return request<TransmitResult>('POST', '/api/v1/payments/fiscal/retry');
}

export function reconciliation(provider?: string): Promise<ReconciliationReport | null> {
  const suffix = provider ? `?provider=${encodeURIComponent(provider)}` : '';
  return request<ReconciliationReport | null>('GET', `/api/v1/payments/reconciliation${suffix}`);
}

export function runReconciliation(
  provider: string,
  businessDay?: string,
): Promise<ReconciliationReport> {
  return request<ReconciliationReport>('POST', '/api/v1/payments/reconciliation/run', {
    provider,
    ...(businessDay ? { businessDay } : {}),
  });
}

export function providers(): Promise<{ providers: readonly ProviderView[] }> {
  return request<{ providers: readonly ProviderView[] }>('GET', '/api/v1/payments/providers');
}

export function providersHealth(): Promise<{
  providers: readonly ProviderHealth[];
  adapters: readonly string[];
}> {
  return request('GET', '/api/v1/payments/providers/health');
}

export interface SaveProviderPayload {
  readonly code: string;
  readonly kind: ProviderView['kind'];
  readonly adapter: string;
  readonly title?: string;
  readonly endpoint?: string | null;
  readonly credentialEnv?: string | null;
  readonly status?: 'active' | 'paused';
  readonly rotateSecret?: boolean;
  /** Объект провайдера: терминал и ОФД у каждого объекта свои. `null` — сеть. */
  readonly siteId?: string | null;
  readonly settings?: Readonly<Record<string, string>>;
}

export function saveProvider(
  payload: SaveProviderPayload,
): Promise<{ provider: ProviderView; secret: string | null }> {
  return request('POST', '/api/v1/payments/providers', payload);
}
